import { AppContext } from "@lightspeed-invoicing/core-client/frontend"
import React, { useEffect, useState } from "react"
import { FormattedMessage, defineMessages, injectIntl } from "react-intl"
import Toggle from "react-toggle"
import { default as GlobalHelper, default as globalHelper } from "../../../common/GlobalHelper"
import LoadingSpinner from "../../../common/spinner/loading.component"
import { Intl } from "../../../state/helpers/IntlProps"
import IntegrationConfigurationValue from "../../../state/integration/IntegrationConfigurationValue"
import {
    FeatureApprovedQuoteAutoExport,
    FeatureContactExport,
    FeatureContactImport,
    FeatureExportOrganizationsOnInvoices,
    FeatureInventorySync,
    FeatureOverrideInvoiceNumber,
    FeatureProductExport,
    FeaturePurchaseOrderTracking,
    FeatureSentInvoiceAutoExport,
} from "../../../state/integration/IntegrationFeatureKeys"
import { QUICKBOOKS_FORM_COMPONENT_MESSAGES } from "../../../utils/integration/quickbooks"
import { integrationKeyQuickbooks } from "../../IntegrationConstants"
import ConfigurationValueComponent from "../ConfigurationValueComponent"
import FeatureToggleComponent from "../FeatureToggleComponent"
import ToggleTooltipComponent from "../ToggleTooltipComponent"

const configToggles = {
    isProductSyncEnabled: false,
    isInventorySyncEnabled: false,
}

const CONFIGURATION_VALUE_EXPORT_PRODUCT_STRATEGY = "exportProductStrategy"
const EXPORT_ALL_AS_NON_INVENTORY = "all-as-non-inventory"
const EXPORT_AS_INVENTORY_AND_NON_INVENTORY = "inventory-and-non-inventory"
const EXPORT_PRODUCT_STRATEGY_MAPPING = defineMessages({
    [EXPORT_ALL_AS_NON_INVENTORY]: {
        id: "integration.form.edit.quickbooks.export_product_strategy.export_all_as_non_inventory",
        defaultMessage: "Export all products as non-inventory"
    },
    [EXPORT_AS_INVENTORY_AND_NON_INVENTORY]: {
        id: "integration.form.edit.quickbooks.export_product_strategy.export_as_inventory_or_non_inventory",
        defaultMessage: "Export products either as inventory or non-inventory"
    },
})

type OwnProps = {
    enabledFeatures: object,
    onFeatureToggled: (featureKey: string) => void,
    configurationValues: IntegrationConfigurationValue[],
    configuration,
    onConfigurationChange: (configuration: object) => void,
    toggleSaveButtonStatus: (enabled: boolean) => void,
    intl: Intl,
    isInventoryModuleEnabled: boolean,
}

const QuickbooksFormComponent = (props: OwnProps) => {

    useEffect(() => {
        initializeDefaultToggles()
    }, [])

    const [isLoading, setIsLoading] = useState<boolean>(true)

    const initializeDefaultToggles = () => {
        if (!props.configuration?.configToggles) {
            props.onConfigurationChange({
                ...props.configuration,
                ...{configToggles: configToggles}
            })
        } else {
            Object.keys(props.configuration.configToggles).map(key => {
                configToggles[key] = props.configuration.configToggles[key]
            })
        }
        setIsLoading(false)
    }

    return (
        <div className="col-12 ui-module">
            {isLoading ?
                <LoadingSpinner/> :
                <div>
                    <div className="form-group row">
                        <label className="col-3 col-form-label text-align-right" htmlFor="realmId">
                            <FormattedMessage
                                id="integration.form.edit.quickbooks.company_id"
                                defaultMessage="Company ID"
                            />
                        </label>
                        <div className="col-9">
                            <input
                                name="realmId"
                                value={props.configuration.realmId}
                                disabled={true}
                                id="realmId"
                                type="text"
                                className="form-control"
                            />
                        </div>
                    </div>
                    {renderSyncToggles(props)}
                    {renderFeatureToggles(props)}
                    {renderConfigurationValues(props)}
                </div>
            }
        </div>
    )
}

const renderSyncToggles = (props: OwnProps) => {

    const integrationsKeysOfInvSync = globalHelper.getIntegrationKeysOfSupportedFeature(FeatureInventorySync)
    const isInvSyncVisible = (integrationsKeysOfInvSync.length == 0 ||
        (integrationsKeysOfInvSync.length == 1 && integrationsKeysOfInvSync.includes(integrationKeyQuickbooks)))

    return Object.keys(configToggles).map((key, i) => {
        const shouldBeDisabled = AppContext.isInventoryEnabled() && ["isProductSyncEnabled", "isInventorySyncEnabled"].includes(key)

        if (key != "isInventorySyncEnabled" || (isInvSyncVisible && configToggles["isProductSyncEnabled"])) {
            return (
                <div key={i} className="form-group row">
                    <label className="col-3 col-form-label text-align-right" htmlFor="itemsImport">
                        {renderFeatureLabel(key)}
                    </label>
                    <div className="col-9 d-flex align-items-center">
                        <span data-tip data-for={"toggleTooltip-anchor" + configToggles[key]}>
                            <Toggle
                                checked={configToggles[key]}
                                defaultChecked={configToggles[key]}
                                icons={false}
                                disabled={shouldBeDisabled}
                                onChange={() => {
                                    configToggles[key] = !configToggles[key]
                                    {
                                        if (!configToggles["isProductSyncEnabled"]) {
                                            configToggles["isInventorySyncEnabled"] = false
                                        }
                                    }
                                    props.onConfigurationChange({
                                        ...props.configuration,
                                        ...{configToggles: configToggles}
                                    })
                                }}
                            />
                        </span>
                        {shouldBeDisabled && <ToggleTooltipComponent 
                            featureKey={configToggles[key]} 
                            message={props.intl.formatMessage(QUICKBOOKS_FORM_COMPONENT_MESSAGES.inventoryTooltipMsg)} />
                        }
                    </div>
                </div>
            )
        }
    })
}

const renderFeatureToggles = (props: OwnProps) => {
    return Object.keys(props.enabledFeatures).map((featureKey, i) => {
        const shouldBeDisabled = AppContext.isInventoryEnabled() && ["purchase-order-tracking"].includes(featureKey)
        if (featureKey == FeatureContactImport && GlobalHelper.hasAnotherIntegrationWithFeatureEnabled(
            integrationKeyQuickbooks,
            FeatureContactImport,
        )) {
            return
        }
        return <FeatureToggleComponent
            key={i}
            disabled={shouldBeDisabled}
            featureKey={featureKey}
            checked={props.enabledFeatures[featureKey]}
            onFeatureToggled={() => {
                props.onFeatureToggled(featureKey)
            }}
            renderFeatureLabel={renderFeatureLabel}
            displayTooltip={shouldBeDisabled}
            tooltip={shouldBeDisabled && props.intl.formatMessage(QUICKBOOKS_FORM_COMPONENT_MESSAGES.inventoryTooltipMsg)}
        />
    })
}

const renderConfigurationValues = (props: OwnProps) => {
    return props.configurationValues.map((c, i) => {
        if (shouldRenderConfigurationAttribute(c.getKey())) {
            if (c.getKey() === CONFIGURATION_VALUE_EXPORT_PRODUCT_STRATEGY) {
                for (const valueKey of Object.keys(c.getValues())) {
                    c.setValueLabel(valueKey, props.intl.formatMessage(EXPORT_PRODUCT_STRATEGY_MAPPING[valueKey]))
                }

                return (
                    <ConfigurationValueComponent
                        key={i}
                        value={props.configuration[c.getKey()] || Object.keys(EXPORT_PRODUCT_STRATEGY_MAPPING)[0]}
                        configurationValue={c}
                        onChange={(newValue) => {
                            const conf = props.configuration
                            conf[c.getKey()] = newValue
                            props.onConfigurationChange(conf)
                        }}
                        renderConfigurationValueLabel={renderConfigurationValueLabel}
                    />
                )
            }

            return <ConfigurationValueComponent
                key={i}
                value={props.configuration[c.getKey()]}
                configurationValue={c}
                onChange={(newValue) => {
                    const conf = props.configuration
                    conf[c.getKey()] = newValue
                    props.toggleSaveButtonStatus(isSaveEnabled(conf))
                    props.onConfigurationChange(conf)
                }}
                renderConfigurationValueLabel={renderConfigurationValueLabel}
            />
        }
    })
}

const renderConfigurationValueLabel = (key: string) => {
    switch (key) {
    case "defaultIncomeAccountId":
        return <FormattedMessage
            id="integration.form.edit.quickbook.default_income_account_id"
            defaultMessage="Default income account (inventory items)"
        />
    case "defaultNonInventoryIncomeAccountId":
        return <FormattedMessage
            id="integration.form.edit.quickbook.default_non_inventory_income_account_id"
            defaultMessage="Default income account (non-inventory items)"
        />
    case "defaultLocationId":
        return <FormattedMessage
            id="integration.form.edit.quickbook.default_location_id"
            defaultMessage="Default location"
        />
    case "defaultAccountsPayableId":
        return <FormattedMessage
            id="integration.form.edit.quickbook.default_ap_account_id"
            defaultMessage="Default accounts payable"
        />
    case "defaultAssetAccountId":
        return <FormattedMessage
            id="integration.form.edit.quickbook.default_asset_account_id"
            defaultMessage="Default asset account"
        />
    case "defaultExpenseAccountId":
        return <FormattedMessage
            id="integration.form.edit.quickbook.default_expense_account_id"
            defaultMessage="Default expense account"
        />
    case "defaultDepositAccountId":
        return <FormattedMessage
            id="integration.form.edit.quickbook.default_deposit_account_id"
            defaultMessage="Default deposit account"
        />
    case CONFIGURATION_VALUE_EXPORT_PRODUCT_STRATEGY:
        return <FormattedMessage
            id="integration.form.edit.quickbook.export_product_strategy"
            defaultMessage="How to export products"
        />
    case "bankTransferPaymentTypeId":
        return <FormattedMessage
            id="integration.form.edit.quickbook.bank_transfer_payment_type_id"
            defaultMessage="Payment type for bank transfer"
        />
    case "cashPaymentTypeId":
        return <FormattedMessage
            id="integration.form.edit.quickbook.cash_payment_type_id"
            defaultMessage="Payment type for cash"
        />
    case "creditCardPaymentTypeId":
        return <FormattedMessage
            id="integration.form.edit.quickbook.credit_card_payment_type_id"
            defaultMessage="Payment type for credit card"
        />
    case "checkPaymentTypeId":
        return <FormattedMessage
            id="integration.form.edit.quickbook.check_payment_type_id"
            defaultMessage="Payment type for cheque"
        />
    default:
        return key
    }
}

const renderFeatureLabel = (key: string) => {
    switch (key) {
    case "isProductSyncEnabled":
        return <FormattedMessage
            id="integration.form.edit.quickbooks.product_sync"
            defaultMessage="Enable product sync"
        />
    case "isInventorySyncEnabled":
        return <FormattedMessage
            id="integration.form.edit.quickbooks.inventory_sync"
            defaultMessage="Enable inventory sync"
        />
    case FeatureContactImport:
        return <FormattedMessage
            id="integration.form.edit.quickbooks.contact_sync"
            defaultMessage="Enable contact import"
        />
    case FeatureContactExport:
        return <FormattedMessage
            id="integration.form.edit.quickbooks.contact_export"
            defaultMessage="Export contacts"
        />
    case FeatureApprovedQuoteAutoExport:
        return <FormattedMessage
            id="integration.form.edit.feature_toggle.approved_quote_auto_export"
            defaultMessage="Auto export approved quote"
        />
    case FeatureExportOrganizationsOnInvoices:
        return <FormattedMessage
            id="integration.form.edit.feature_toggle.export_organizations_on_invoices"
            defaultMessage="Contact on exported invoices should be the organization when possible"
        />
    case FeatureOverrideInvoiceNumber:
        return <FormattedMessage
            id="integration.form.edit.feature_toggle.override_invoice_number"
            defaultMessage="Override invoice number"
        />
    case FeatureProductExport:
        return <FormattedMessage
            id="integration.form.edit.feature_toggle.product_export"
            defaultMessage="Export products"
        />
    case FeatureSentInvoiceAutoExport:
        return <FormattedMessage
            id="integration.form.edit.feature_toggle.sent_invoice_auto_export_sync"
            defaultMessage="Auto export of sent invoice"
        />
    case FeaturePurchaseOrderTracking:
        return <FormattedMessage
            id="integration.form.edit.feature_toggle.purchase_order_tracking_description"
            defaultMessage="Create and track PO from orders"
        />
    default:
        return key
    }
}

const shouldRenderConfigurationAttribute = (configurationValueKey: string) => {
    if (configurationValueKey === "priceListCreditMemoPaymentTypeId" && !GlobalHelper.hasGolfIntegration()) {
        return false
    }
    return true
}

const isSaveEnabled = (configuration: object) => {
    if (!GlobalHelper.hasGolfIntegration()) {
        delete configuration["priceListCreditMemoPaymentTypeId"]
        delete configuration["onAccountPaymentTypeId"]
    }
    return !(Object.keys(configuration).filter(key => configuration[key] === "").length > 0)
}

export default injectIntl(QuickbooksFormComponent)


// WEBPACK FOOTER //
// ./src/integration/form/quickbooks/QuickbooksFormComponent.tsx